import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {Project} from '../interfaces/project';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import 'firebase/firestore';
import {DraftService} from '../services/draft.service';
import {ProjectFormNavComponent} from "./project-form-nav/project-form-nav.component";
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";
import {AnnualSchoolProfile} from "../interfaces/annual-school-profile";
import {AnnualSchoolProfileService} from "../services/annual-school-profile.service";
import {ActivatedRoute} from "@angular/router";
import {doc, Firestore, getDoc} from "@angular/fire/firestore";

/**
 * ProjectFormComponent - Multi-step wizard form for environmental project submissions
 *
 * This component provides a comprehensive form interface for schools to submit
 * environmental projects including food waste, composting, recycling, water conservation,
 * and outdoor learning initiatives.
 *
 * @component
 * @selector app-project-form
 * @templateUrl ./project-form.component.html
 * @styleUrl ./project-form.component.css
 *
 * @example
 * ```html
 * <app-project-form
 *   [formData]="projectData"
 *   (formSubmitted)="handleSubmit($event)">
 * </app-project-form>
 * ```
 */
@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrl: './project-form.component.css'
})

export class ProjectFormComponent {
  @ViewChild(ProjectFormNavComponent)
  nav!: ProjectFormNavComponent;

  // Input/Output Properties
  @Input() formData!: Project;
  @Output() formSubmitted = new EventEmitter<any>();
  projects: any[] = [];

  // Search & Filter Methods
  filteredProjects: any[] = [];
  router: any;
  projectForm: any;
  annualProfile: AnnualSchoolProfile[] = [];

  currentTeacher: Teacher | null = null;

  // Constructor
  constructor(
    public afAuth: AngularFireAuth,
    private draftService: DraftService,
    private teacherService: TeacherService,
    private route: ActivatedRoute,
    private annualService: AnnualSchoolProfileService,
    private firestore: Firestore
  ) {
  }

  /**
   * Angular lifecycle hook - called after component initialization
   *
   * Initializes the form, loads schools, loads projects, and sets up auto-save
   *
   * @lifecycle
   * @returns {void}
   */
  async ngOnInit() {

    this.initializeForm();
    await this.getSchools();
    await this.loadProjects();
    await this.loadTeachers();
    this.annualProfile = await this.annualService.getAllProfiles();
    this.generateSchoolYears();

    const year = this.route.snapshot.queryParamMap.get('schoolYear');
    if (year) {
      this.formData.schoolYear = year;
    }

    this.fillExtraRepresentatives();

    const draft = history.state?.draftProfile;
    if (draft) {
      this.formData = {...this.formData, ...draft};
    }
    const draftId = this.route.snapshot.queryParamMap.get('draftId');
    if (draftId) {
      await this.loadDraft(draftId);
    }
  }

  currentDraft: Project | null = null;

  async loadDraft(draftId: string) {
    try {
      const docRef = doc(this.firestore, 'projects', draftId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        this.currentDraft = {id: docSnap.id, ...docSnap.data()} as Project;
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }

  /**
   * Loads all projects from Firestore for search functionality
   *
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If Firestore query fails
   */
  async loadProjects() {
    const snapshot = await firebase.firestore().collection('projects').get();
    this.projects = snapshot.docs.map(doc => doc.data());
    this.filteredProjects = [];
  }

  getAssociatedAnnualProfileTeacher(): string {
    if (!this.formData.school || !this.annualProfile) return '';

    const profile = this.annualProfile.find(p =>
      p.school.trim().toLowerCase() === this.formData.school.trim().toLowerCase() &&
      !p.isDeleted
    );
    if (!profile?.ghsRepresentativeName) return '';
    return profile.ghsRepresentativeName.replace(/\s*teacher$/i, '');
  }

  // Dynamic Field Methods

  /**
   * Shows next additional representative field
   * Reveals representative fields sequentially up to a maximum of 2 extra fields
   * @returns {void}
   */
  showNextRepField() {
    if (!this.formData.extraRep1Visible) {
      this.formData.extraRep1Visible = true;
    } else if (!this.formData.extraRep2Visible) {
      this.formData.extraRep2Visible = true;
    }
  }

  gardenOptions = ['Pollinator', 'Edible', 'Herbs', 'Flowers', 'Other'];
  compostingSysOptions = ['Tumbler', '3-bin system'];
  compostUsedOptions = ['School garden', 'Bag & relocate'];
  foodItemsOptions = ['School garden', 'Bag & relocate'];
  foodPantryItemsOptions = ['Share Table', 'Donors'];

  toggleArrayValue(array: string[], value: string, checked: boolean | Event) {
    const isChecked = typeof checked === 'boolean' ? checked : (checked.target as HTMLInputElement).checked;
    if (isChecked) {
      if (!array.includes(value)) array.push(value);
    } else {
      const index = array.indexOf(value);
      if (index > -1) array.splice(index, 1);
    }
  }

  // Form Initialization

  /**
   * Initializes form data with default values
   *
   * Creates a new Project object with all required fields set to defaults.
   * Only initializes if formData is null to avoid overwriting existing data.
   *
   * @returns {void}
   */
  initializeForm() {
    const now = new Date();

    if (this.formData == null) {
      this.formData = {
        status: 'draft', //default
        isDeleted: false,
        recoverableItemTypes: [] as string[],
        title: '',
        school: '',
        schoolYear: '',
        visibility: 'Everyone',
        owner: '',
        customSchool: '',
        representativeName: '',
        representativeEmail: '',
        teamMember2Email: '',
        teamMember3Email: '',
        numStudentsInClass: 0,
        numTeachersInSchool: 0,
        isPartOfCluster: null,
        teachingLevel: '',
        grades: [] as string[],
        selectedCourses: [] as string[],
        extraRep1Visible: false,
        extraRep2Visible: false,
        rep2Email: '',
        rep3Email: '',

        dateSubmitted: now.toISOString(),
        startDate: '',
        endDate: 'Active',
        dateEdited: now.toISOString(),

        // Section 1 - Project Details
        projectClubs: '',
        projectDesc: '',
        impact: '',
        numTeachersInProject: 0,
        numStudentsInProject: 0,
        projectStatus: '',

        // Section 2 - Community Partners
        communityPartners: '',
        hadFieldTrip: null,
        clubs: '',
        fieldtrip: '',
        funderAndDonorNameAndComments: '',
        amount: 0,
        communityPartnersContactInfo: '',
        grantSupplies: '',
        numCommunityMembers: 0,
        numParentsEngaged: 0,
        numVolunteerHours: 0,

        // Section 3 - Food Waste
        foodWasteDesc: '',
        foodWasteStrategies: '',
        foodWastePounds: 0,
        shareTablePounds: 0,
        numStudentsInFoodWaste: 0,
        numTeachersInFoodWaste: 0,

        // Section 4 - Composting
        numStudentsInComposting: 0,
        numTeachersInComposting: 0,
        support: [] as string[],
        supportOther: '',

        // Section 5 - Gardens
        gardens: '',
        gardensDetails: '',
        gardenFoodDonated: false,
        foodDistributionDetails: '',
        numStudentsInGarden: 0,
        numTeachersInGarden: 0,

        // Garden Types
        gardenTypes: [],
        gardenTypeOtherComment: '',
        familiesServedByGarden: 0,

        // Students/Families provided produce
        studentsProvidedProduce: false,
        productPoundsDonated: 0,

        // Section 6 - Indoor Growing
        indoorGrowing: '',
        indoorDetails: '',

        // Section 7 - Grants
        grants: '',
        grantsDetails: '',
        grantUtilizationDetails: '',

        // Section 8 - Recycling
        recycling: '',
        recyclingDetails: '',
        numStudentsInRecycling: 0,
        numTeachersInRecycling: 0,
        recyclingSelected: false,
        recyclingOptions: [] as string[],
        compostingSelected: false,
        outdoorSelected: false,
        indoorSelected: false,

        // Section 9 - Water
        waterTopics: '',
        waterProjectDescription: '',
        numStudentsInWater: 0,
        numTeachersInWater: 0,
        numHoursInWater: 0,
        numParticipationHours: 0,
        images: [] as string[],

        acknowledgement: false,

        /// Category toggles
        foodwasteToggle: false,
        educationalcampaign1: false,
        sharetable1: false,
        donation: false,
        pantry: false,
        fwasteaudit: false,
        foodOtherCheck: false,
        foodOtherComment: '',


        // Food Waste Audit fields
        fwaAuditDate: '',
        fwaStudentsServed: 0,
        fwaMenuItems: '',
        fwaRecoverableItemsList: '',
        fwaRecoverables: 0,
        fwaMilk: 0,
        fwaOtherLiquids: 0,
        fwaFruitsVegetables: 0,
        fwaMeatsGrainsOther: 0,
        fwaTotalFoodWaste: 0,
        fwaFoodWastePerStudent: 0,
        fwaTrashPackaging: 0,
        fwaRecycling: 0,
        fwaParentHelpers: 0,
        fwaStudentsParticipating: 0,
        fwaCommunityMembers: 0,
        fwaCommunityMembersIdentify: '',
        fwaStudentFeedback: [] as string[],
        fwaIsFirstAudit: false,
        fwaComparisonToPrevious: '',
        fwaWasteAttribution: '',
        fwaShareTableBreakfast: false,
        fwaShareTableLunch: false,
        fwaShareRefrigerator: false,
        fwaFoodPantryOperating: false,
        fwaShareTableToFoodPantry: false,
        fwaSolutionsImplemented: '',
        fwaGrantReceived: false,
        fwaGrantValue: 0,

        // Composting details
        lbsFoodScrapsComposted: 0,
        foodScrapsDonated: '',
        foodScrapsRecipients: '',
        compostingSystem: [],
        compostUsed: [],
        compostUsedGarden: false,
        compostUsedBag: false,

        // Outdoor learning
        garden: false,
        fish: false,
        chicken: false,
        class: false,
        outdoorOtherCheck: false,
        outdoorOtherComment: '',
        gardenLearning: '',
        gardenTeachers: 0,
        totalStudentsImpacted: 0,
        gardenBeds: 0,
        familiesServedOption: '',
        pantryFamiliesOption: '',
        pantryFamiliesCount: 0,
        grantReceivedOption: '',
        grantProvider: '',
        grantAmount: 0,
        pollinatorProject: '',
        pollinatorCensus: false,
        gardenVolunteers: 0,
        orgVolunteersOption: '',
        orgVolunteersCount: 0,
        projectActive: '',
        aksTopics: '',
        indoorSystem: '',
        indoorGrowingSystem: '',
        indoorStudents: 0,
        trails: false,
        greenhouse: false,
        compostSystem: false,
        pollinatorCensusOption: false,

        // Indoor growing systems
        hydroponics1: false,
        aquaponics1: false,
        aeroponics1: false,
        towers1: false,
        indoorOtherCheck: false,
        indoorOtherComment: '',

        // Water pollution (OLD - kept for backwards compatibility)
        WaterToggle: false,
        watereducationalcampaign1: false,
        communityoutreach: false,
        adoptARoad: false,
        adoptAstream: false,
        StreamCleanUp: false,
        landUsesErosion: false,
        tour: false,
        TreatmentPlant: false,
        drinkingWaterPlant: false,
        tourOther: false,
        waterRelated: false,
        inHouseProgram: false,
        waterStreamMonitor: false,
        biological1: false,
        chemical1: false,
        cleanup1: false,
        streamMonitorOther: false,
        annualWatershed: false,
        waterOtherCheck: false,
        waterOtherComment: '',

        // Recycling
        paperAndCardboard: false,
        cardboard1: false,
        textiles1: false,
        plastics1: false,
        aluminumcans1: false,
        recycleOtherCheck: false,
        recycleOtherComment: '',
        recyclingContractOption: '',
        recyclingCompany: '',
        recyclingItems: '',
        recyclingCharge: 0,
        textileMoney: 0,
        textileLbs: 0,
        recyclingEventsToggle: false,
        chipperEvent: false,
        earthDayEvent: false,
        americaRecyclesEvent: false,
        recyclingEventsOtherCheck: false,
        recyclingEventsOtherComment: '',

        // Renewable energy
        renewableToggle: false,
        solar1: false,
        wind1: false,
        water1: false,
        hydroelectric1: false,
        biomass1: false,
        geothermal1: false,
        renewableOtherCheck: false,
        renewableOtherComment: '',
        solarInstructionComment: '',
        solarUtilityComment: '',
        windInstructionComment: '',
        windUtilityComment: '',
        waterInstructionComment: '',
        waterUtilityComment: '',

        // Recycling Events
        chipperDate: '',
        chipperResults: '',
        earthDayDate: '',
        earthDayResults: '',
        americaRecyclesDate: '',
        americaRecyclesResults: '',
        recyclingEventsOtherDate: '',
        recyclingEventsOtherResults: '',

        // Shared table
        shareTableBreakfast: '',
        shareTableLunch: '',
        shareTableCommonItems: '',

        // Food Pantry
        foodItemsFrom: [],
        donorName: '',
        familiesServedAnnually: 0,

        // Food Waste Audit
        foodAuditDate: '',
        foodAuditNumLunchesServed: 0,
        foodAuditMenuItems: '',

        // Fish hatchery
        fishHatcheryNumReleased: 0,
        fishHatcheryTypeComment: '',

        // Chicken and other farm animals
        eggsProvided: '',
        familiesServedEggs: 0,
        lbsEggsDonated: 0,

        // Outdoor classroom
        numGardenLearningAreas: 0,
        sizeOfGarden: '',

        // Trails
        trailDescription: '',

        // Recycling Items
        paperCardboardLbs: 0,
        paperAndCardboardCompany: '',
        paperAndCardboardCost: 0,
        textileLbsCollected: 0,
        textileCompany: '',
        textileDollarsEarned: 0,
        plasticLbsCollected: 0,
        plasticCollectedBy: '',
        aluminumLbsCollected: 0,
        aluminumCollectedBy: '',
        recyclingComment: '',
        otherLbsCollected: 0,
        otherCollectedBy: '',

        // Educational Campaign
        campaignContentComment: '',
        //Greenhouses
        greenhouseComment: '',

        // Composting GardenOnly
        gardenScrapsCompostedLbs: 0,

        // Pollinator Census
        pollinatorCountApprox: 0,
        pollinatorComparisonComment: '',
        pollinatorOther: false,
        pollinatorOtherComment: '',

        // Indoor growing systems detail
        indoorSystemDescription: '',
        indoorItemsGrown: '',
        indoorFoodDonatedLbs: 0,

        hydroponicsSystemDescription: '',
        hydroponicsItemsGrown: '',
        hydroponicsFoodDonatedLbs: 0,

        aquaponicsSystemDescription: '',
        aquaponicsItemsGrown: '',
        aquaponicsFoodDonatedLbs: 0,

        aeroponicsSystemDescription: '',
        aeroponicsItemsGrown: '',
        aeroponicsFoodDonatedLbs: 0,

        towersSystemDescription: '',
        towersItemsGrown: '',
        towersFoodDonatedLbs: 0,

        // Watershed Projects (NEW - main category)
        watershedProjectsMainToggle: false,
        waterEducationalCampaign: false,
        adoptARoadWatershed: false,
        adoptAStreamCleanup: false,
        adoptAStreamMonitoring: false,
        streamRestoration: false,

        // Other Community Watershed Projects
        WatershedProjectsToggle: false,
        greatDaysService: false,
        wetlandCleanup: false,
        yellowRiverTrashBash: false,
        shoreSweep: false,
        localWaterEvent: false,
        localWaterEventComment: '',
        gwinnettServiceDept: false,
        supportCharity: false,
        //studentProjects: false,
        elachee: false,

        // Watershed Field Trips
        watershedTripsToggle: false,
        wasteWaterPlant: false,
        watersheddrinkingWaterPlant: false,
        theWaterTower: false,
        deptOfWaterResources: false,
        gwinnettCleanBeautiful: false,
        watershedOtherCheck: false,
        watershedOtherComment: '',

        // Watershed Project Details
        watershedEduDetail: '',
        watershedEduTargetStudents: false,
        watershedEduTargetParents: false,

        // Adopt-A-Road Details
        watershedRoadName: '',
        watershedRoadTrashLbs: 0,

        // Stream Clean-Up Details
        watershedStreamCleanupName: '',
        watershedStreamCleanupLbs: 0,

        // Stream Monitoring Details
        watershedMonitorBiological: false,
        watershedMonitorChemical: false,
        watershedMonitorOther: false,
        watershedMonitorOtherComment: '',
        watershedMonitorStreamName: '',

        // Restoration/Erosion Control Details
        watershedRestorationStreamName: '',

        // Watershed Commentaries
        watershedOtherResultComment: '',
        wasteWaterPlantComment: '',
        drinkingWaterPlantComment: '',
        waterTowerComment: '',
        deptOfWaterResourcesComment: '',
        gwinnettCleanBeautifulComment: '',
        watershedOtherTripComment: '',
        watershedTripsResultComment: '',

        // Other Community Watershed Project Commentaries
        greatDaysServiceComment: '',
        wetlandCleanupComment: '',
        yellowRiverTrashBashComment: '',
        shoreSweepComment: '',
        localWaterEventComment2: '',
        gwinnettServiceDeptComment: '',
        supportCharityComment: '',
        studentProjectsComment: '',

        // Filter project
        pastProject: '',

        // Project other
        relatedProject: '',
        relatedProjectComment: '',
        eventDuration: 0,
        foodWasteSelections: [] as string[],
        outdoorLearningSelections: [] as string[],
        indoorGrowingSelections: [] as string[],
        waterSelections: [] as string[],
        recyclingSelections: [] as string[],
        renewableSelections: [] as string[],
        Saved: false,
      };
    }
  }

  otherSchoolSelected = false;
  currentStep = 1;
  totalSteps = 43;
  schools: any[] = [];
  selectedImages: File[] = [];

  /**
   * Available school years for selection
   * @type {string[]}
   * @todo Make this automatic based on current date
   */
  schoolYears: string[] = [];

  generateSchoolYears() {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    this.schoolYears = [];
    for (let start = 2020; start <= currentYear; start++) {
      const end = start + 1;
      if (end <= nextYear) {
        this.schoolYears.push(`${start}-${end}`);
      }
    }
  }

  grades = ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  // Utility Methods
  /**
   * Returns the first half of an array
   *
   * Used for splitting checkbox lists into two columns
   *
   * @param {string[]} arr - Array to split
   * @returns {string[]} First half of the array (rounded up for odd lengths)
   */
  getFirstHalf(arr: string[]): string[] {
    return arr.slice(0, Math.ceil(arr.length / 2));
  }

  /**
   * Returns the second half of an array
   *
   * Used for splitting checkbox lists into two columns
   *
   * @param {string[]} arr - Array to split
   * @returns {string[]} Second half of the array
   */
  getSecondHalf(arr: string[]): string[] {
    return arr.slice(Math.ceil(arr.length / 2));
  }

  /**
   * Loads school list from Firestore
   *
   * Fetches all schools, appends " School" to names, and sorts alphabetically
   *
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If Firestore query fails
   */
  async getSchools() {
    const snapshot = await firebase.firestore().collection('school').get();
    snapshot.forEach((doc) => this.schools.push((doc.data())));
    this.schools.forEach((doc) => {
      if (doc.name) {
        doc.name = doc.name + " School";
      }
    });
    this.schools.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Event Handlers
  /**
   * Handles file selection for project images
   *
   * Validates that no more than 10 images are selected
   *
   * @param {Event} event - The file input change event
   * @returns {void}
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const selectedFiles = Array.from(input.files);

    if (this.selectedImages.length + selectedFiles.length > 10) {
      alert("You can only upload up to 10 images.");
      input.value = '';
      this.selectedImages = [];
      return;
    }

    this.selectedImages = [];
    this.selectedImages.push(...selectedFiles);
  }

  /**
   * Handles school selection change
   *
   * Sets flag when "Other" school option is selected
   *
   * @returns {void}
   */

  /*onSchoolChanged() {
    this.otherSchoolSelected = this.formData.school === 'other'
  }*/

  /**
   * Toggles grade level selection
   *
   * @param {string} grade - The grade level to toggle
   * @param {Event} event - The checkbox change event
   * @returns {void}
   */
  toggleGradeSelection(grade: string, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.formData.grades.push(grade);
    } else {
      this.formData.grades = this.formData.grades.filter(
        selectedGrade => selectedGrade !== grade
      );
    }
  }

  /**
   * Toggles support/community activity selection
   *
   * @param {string} option - The support option to toggle
   * @param {Event} event - The checkbox change event
   * @returns {void}
   */
  toggleSupportSelection(option: string, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.formData.support.includes(option)) {
        this.formData.support.push(option);
      }
    } else {
      this.formData.support = this.formData.support.filter(o => o !== option);
    }
  }

  // Category Collection Methods

  /**
   * Collects all selected categories and subcategories into arrays
   *
   * This method aggregates user selections across all category sections
   * (food waste, outdoor learning, indoor growing, water, recycling, renewable)
   * and populates corresponding selection arrays in formData.
   *
   * Called before form submission to compile final selections.
   *
   * @returns {void}
   */
  collectSelectedCategories() {
    // ✅ Food Waste
    this.formData.foodWasteSelections = [];
    if (this.formData.educationalcampaign1) this.formData.foodWasteSelections.push('Educational Campaign');
    if (this.formData.sharetable1) this.formData.foodWasteSelections.push('Share Table');
    if (this.formData.compostingSelected) this.formData.foodWasteSelections.push('Composting');
    if (this.formData.donation) this.formData.foodWasteSelections.push('Donation');
    if (this.formData.pantry) this.formData.foodWasteSelections.push('Food Pantry');
    if (this.formData.fwasteaudit) this.formData.foodWasteSelections.push('Food Waste Audit');
    if (this.formData.foodOtherCheck && this.formData.foodOtherComment)
      this.formData.foodWasteSelections.push(`Other: ${this.formData.foodOtherComment}`);

    // ✅ Outdoor Learning
    this.formData.outdoorLearningSelections = [];
    if (this.formData.garden) this.formData.outdoorLearningSelections.push('Gardens');
    if (this.formData.fish) this.formData.outdoorLearningSelections.push('Fish Hatchery');
    if (this.formData.chicken) this.formData.outdoorLearningSelections.push('Chickens/Farm Animals');
    if (this.formData.class) this.formData.outdoorLearningSelections.push('Outdoor Classroom');
    if (this.formData.outdoorOtherCheck && this.formData.outdoorOtherComment)
      this.formData.outdoorLearningSelections.push(`Other: ${this.formData.outdoorOtherComment}`);

    // ✅ Indoor Growing
    this.formData.indoorGrowingSelections = [];
    if (this.formData.hydroponics1) this.formData.indoorGrowingSelections.push('Hydroponics');
    if (this.formData.aquaponics1) this.formData.indoorGrowingSelections.push('Aquaponics');
    if (this.formData.aeroponics1) this.formData.indoorGrowingSelections.push('Aeroponics');
    if (this.formData.towers1) this.formData.indoorGrowingSelections.push('Grow Towers');
    if (this.formData.indoorOtherCheck && this.formData.indoorOtherComment)
      this.formData.indoorGrowingSelections.push(`Other: ${this.formData.indoorOtherComment}`);

    // ✅ Watershed Projects (NEW)
    this.formData.waterSelections = [];
    if (this.formData.waterEducationalCampaign)
      this.formData.waterSelections.push('Water Educational Campaign');
    if (this.formData.adoptARoadWatershed)
      this.formData.waterSelections.push('Adopt-A-Road');
    if (this.formData.adoptAStreamCleanup)
      this.formData.waterSelections.push('Adopt-A-Stream Clean-up');
    if (this.formData.adoptAStreamMonitoring)
      this.formData.waterSelections.push('Adopt-A-Stream Monitoring');
    if (this.formData.streamRestoration)
      this.formData.waterSelections.push('Stream Restoration/Erosion Control');

    // ✅ Recycling
    this.formData.recyclingSelections = [];
    if (this.formData.paperAndCardboard) this.formData.recyclingSelections.push('Paper');
    if (this.formData.cardboard1) this.formData.recyclingSelections.push('Cardboard');
    if (this.formData.textiles1) this.formData.recyclingSelections.push('Textiles');
    if (this.formData.plastics1) this.formData.recyclingSelections.push('Plastics');
    if (this.formData.aluminumcans1) this.formData.recyclingSelections.push('Aluminum Cans');
    if (this.formData.recycleOtherCheck && this.formData.recycleOtherComment)
      this.formData.recyclingSelections.push(`Other: ${this.formData.recycleOtherComment}`);

    // ✅ Renewable
    this.formData.renewableSelections = [];
    if (this.formData.solar1) this.formData.renewableSelections.push('Solar');
    if (this.formData.wind1) this.formData.renewableSelections.push('Wind');
    if (this.formData.water1) this.formData.renewableSelections.push('Water');
    if (this.formData.hydroelectric1) this.formData.renewableSelections.push('Hydroelectric');
    if (this.formData.biomass1) this.formData.renewableSelections.push('Biomass');
    if (this.formData.geothermal1) this.formData.renewableSelections.push('Geothermal');
    if (this.formData.renewableOtherCheck && this.formData.renewableOtherComment)
      this.formData.renewableSelections.push(`Other: ${this.formData.renewableOtherComment}`);
  }

  // Calculation Methods

  /**
   * Calculates total food waste from individual waste components
   *
   * Sums all food waste categories:
   * - Recoverables (unpeeled/unopened items)
   * - Milk
   * - Other liquids
   * - Fruits and vegetables
   * - Meats, grains, and other foods
   *
   * Also triggers calculation of waste per student.
   *
   * @returns {void}
   */
  calculateTotalFoodWaste() {
    this.formData.fwaTotalFoodWaste =
      (this.formData.fwaRecoverables || 0) +
      (this.formData.fwaMilk || 0) +
      (this.formData.fwaOtherLiquids || 0) +
      (this.formData.fwaFruitsVegetables || 0) +
      (this.formData.fwaMeatsGrainsOther || 0);

    this.calculateFoodWastePerStudent();
  }

  /**
   * Calculates food waste per student
   *
   * Divides total food waste by number of students served, rounded to 2 decimal places.
   * Returns 0 if no students are recorded to avoid division by zero.
   *
   * @returns {void}
   */
  calculateFoodWastePerStudent() {
    const total = this.formData.fwaTotalFoodWaste || 0;
    const students = this.formData.fwaStudentsServed || 0;

    if (students > 0) {
      this.formData.fwaFoodWastePerStudent =
        Math.round((total / students) * 100) / 100;
    } else {
      this.formData.fwaFoodWastePerStudent = 0;
    }
  }

  // Form Submission & Save Methods

  /**
   * Submits the completed form
   *
   * Collects all selected categories, sets status to 'submitted',
   * and emits the formSubmitted event with form data and selected images.
   *
   * @fires ProjectFormComponent#formSubmitted
   * @returns {void}
   */

  @Output() submittedChange = new EventEmitter<boolean>();

  submitted = false;
  submitForm() {
    this.submitted = true;
    this.submittedChange.emit(this.submitted);
    this.collectSelectedCategories();
    this.formData.status = 'submitted';
    this.formSubmitted.emit({formData: this.formData, selectedImages: this.selectedImages});
  }

  /**
   * Saves the form as a draft to Firestore
   *
   * Sets status to 'draft', saves via DraftService, and navigates to profile page.
   * Shows success/error alerts based on save result.
   *
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If save operation fails
   */
  async saveDraft() {
    try {
      this.formData.status = 'draft';
      const draftId = await this.draftService.saveDraft(this.formData);
      this.formData.id = draftId;
      alert('Draft saved successfully!');
      this.formData.Saved = true;
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft. Please try again.');
    }
    this.router.navigate(['/profile']);
    this.collectSelectedCategories();//
    try {
      // Set the save state to indicate saving in progress
      this.formData.Saved = true;
      this.formData.status = 'draft';
      this.formData.dateEdited = new Date().toISOString();

      // Save to Firebase through DraftService
      const draftId = await this.draftService.saveDraft(this.formData);
      this.formData.id = draftId;

      // Mark as successfully saved
      this.formData.Saved = true;

      alert('Draft saved successfully!');
      //this.router.navigate(['/profile']);
    } catch (error) {
      console.error('Error saving draft:', error);
      this.formData.Saved = false; // Mark as not saved due to error
      alert('Error saving draft. Please try again.');
    }
  }

  /**
   * Marks form as having unsaved changes
   *
   * Called when any form field is modified
   *
   * @returns {void}
   */
  onFormChange() {
    this.formData.Saved = false;
  }

  /**
   * Auto-saves the form draft if there are unsaved changes
   *
   * Triggered by interval timer (every 30 seconds).
   * Only saves if form has unsaved changes and a title is present.
   *
   * @async
   * @returns {Promise<void>}
   */
  async autoSave() {
    if (!this.formData.Saved && this.formData.title) { // Only auto-save if there's content and changes
      try {
        this.formData.status = 'draft';
        this.formData.dateEdited = new Date().toISOString();

        const draftId = await this.draftService.saveDraft(this.formData);
        this.formData.id = draftId;
        this.formData.Saved = true;

        console.log('Auto-saved draft successfully');
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }
  }

  /**
   * Saves form data to Firestore manually
   *
   * Similar to saveDraft but with explicit success/error handling
   *
   * @async
   * @returns {Promise<string>} The draft document ID
   * @throws {Error} If save operation fails
   */
  async saveToFirebase() {
    try {
      this.formData.Saved = false;
      this.formData.status = 'draft';
      this.formData.dateEdited = new Date().toISOString();

      const draftId = await this.draftService.saveDraft(this.formData);
      this.formData.id = draftId;
      this.formData.Saved = true;

      alert('Saved to Firebase successfully!');
      return draftId;
    } catch (error) {
      console.error('Error saving to Firebase:', error);
      this.formData.Saved = false;
      alert('Failed to save to Firebase. Please try again.');
      throw error;
    }
  }

  // Local Storage Methods (Legacy)

  /**
   * Saves draft data to localStorage
   *
   * @deprecated Use saveToFirebase instead
   * @returns {void}
   */
  saveData() {
    // 1. Create draft data object
    const draftData = {
      data: this.formData,
      timestamp: new Date().toISOString()
    };

    // 2. Save the draft to localStorage
    localStorage.setItem('RecentDraft', JSON.stringify(draftData));

    // 3. Create a URL slug for this draft (e.g., /projects/my-project)
    const draftUrl = `/projects/${encodeURIComponent(
      this.formData.title.replace(/\s+/g, '-').toLowerCase()
    )}`;

    // 4. Save the draft URL separately for easy retrieval
    localStorage.setItem('RecentDraftUrl', draftUrl);

    // 5. (Optional) Navigate to the saved draft link automatically
    // this.router.navigate([draftUrl]);

    console.log('Draft saved:', draftData);
    console.log('Draft URL:', draftUrl);
  }

  /**
   * Retrieves draft data from localStorage
   *
   * @deprecated Use DraftService to load from Firestore
   * @returns {void}
   */
  retrieveData() {
    const storedDraft = localStorage.getItem('RecentDraft');
    const storedDraftUrl = localStorage.getItem('RecentDraftUrl');

    if (storedDraft) {
      const draftData = JSON.parse(storedDraft);

      //Restore form fields
      this.projectForm.patchValue(draftData.data);
      console.log('Draft loaded:', draftData);

      // Navigate only if you have a valid route defined
      if (storedDraftUrl) {
        // Make sure this path exists in your app-routing.module.ts
        this.router.navigate([storedDraftUrl]);
      } else {
        console.warn('No stored draft URL found, staying on current page.');
      }
    } else {
      console.warn('No saved draft found in localStorage.');
    }

  }

  studentFeedbackOptions: string[] = [
    'Food Unappealing (taste, texture, hard to eat, raw vs cooked, quality concerns- undercooked/cold)',
    'Time Constraints (long lines, lunch period too short, excessive socializing)',
    'Offer vs Serve (OVS) Confusion (Student did not want what was on their plate)',
    'Lack of Hunger / Fullness (not hungry or too much food)'
  ];

  toggleFeedback(option: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (!this.formData.fwaStudentFeedback) {
      this.formData.fwaStudentFeedback = [];
    }
    if (checked) {
      if (!this.formData.fwaStudentFeedback.includes(option)) {
        this.formData.fwaStudentFeedback.push(option);
      }
    } else {
      this.formData.fwaStudentFeedback = this.formData.fwaStudentFeedback.filter(o => o !== option);
    }
  }


  activeSteps: number[] = [];

  toggleStep(step: number) {
    const isCategory = step === 3;
    const resultSteps = [
      6, 7, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 36, 37,
      38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56
    ];
    const isResult = resultSteps.includes(step);

    if (isCategory) {
      const currentResults = this.activeSteps.filter(s => resultSteps.includes(s));
      if (this.activeSteps.includes(step)) {
        this.activeSteps = currentResults; // remove category
      } else {
        this.activeSteps = [...currentResults, step]; // add category
      }
    } else if (isResult) {
      const currentCategory = this.activeSteps.filter(s => s === 3);
      const otherResults = this.activeSteps.filter(s => resultSteps.includes(s) && s !== step);

      if (this.activeSteps.includes(step)) {
        this.activeSteps = [...currentCategory, ...otherResults];
      } else {
        this.activeSteps = [...currentCategory, ...otherResults, step];
      }
    } else {
      this.activeSteps = [step];
    }
  }

  getStepTitle(): string {
    switch (this.currentStep) {
      case 1:
        return 'General Information';
      case 2:
        return 'Project Details and Status';
      case 3:
        return 'Select Category';
      case 4:
        return 'Community Partners & Related Projects';
      case 5:
        return 'Food Waste Reduction';
      case 6:
        return 'Composting';
      case 7:
        return 'Outdoor Learning Spaces';
      case 8:
        return 'Indoor Growing Systems';
      case 9:
        return 'Recycling';
      case 10:
        return 'Renewable Energy';
      case 11:
        return 'Recycling Events';
      case 12:
        return 'Share table';
      case 13:
        return 'Food Pantry'
      case 14:
        return 'Food Waste Audit';
      case 15:
        return 'Fishing Hatchery';
      case 16:
        return 'Chickens and other farm animals - goats, rabbits, other';
      case 17:
        return 'Outdoor Classroom/Gazebos/seating';
      case 18:
        return 'Trails/forested area';
      case 19:
        return 'Recycling Items';
      case 20:
        return 'Educational Campaign';
      case 21:
        return 'GreenHouse(s)';
      case 22:
        return 'Composting Garden';
      case 23:
        return 'SouthWest Pollinator Census';
      case 24:
        return 'Indoor Growing Systems Details';
      case 25:
        return 'Water Educational Campaign';
      case 26:
        return 'Adopt-A-Road';
      case 27:
        return 'Adopt-A-Stream Clean-up';
      case 28:
        return 'Adopt-A-Stream Monitoring';
      case 29:
        return 'Stream Restoration/Erosion Control';
      case 30:
        return 'Waste Water Plant';
      case 31:
        return 'Drinking Water Plant';
      case 32:
        return 'Water Tower';
      case 33:
        return 'Dept of Water Resources';
      case 34:
        return 'Gwinnett Clean & Beautiful';
      case 35:
        return 'Watershed Other Commentary';
      case 36:
        return 'GC&B Great Days of Service';
      case 37:
        return 'GC&B Wetland Cleanup';
      case 38:
        return 'Yellow River Trash Bash';
      case 39:
        return 'Shore Sweep';
      case 40:
        return 'Local Water Event';
      case 41:
        return 'Gwinnett Service Dept';
      case 42:
        return 'Support Charity';
      case 43:
        return 'Student Projects';
      default:
        return 'New Project';
    }
  }

  teacherName: any[] = [];
  teacherFullList: any[] = [];
  representativeList: any[] = [];

  async loadTeachers() {
    this.currentTeacher = await this.teacherService.getTeacher();

    const teachersByRole = await this.teacherService.getTeachersByRole() ?? [];
    const allTeachers = await this.teacherService.getAllTeachers() ?? [];

    this.teacherFullList = allTeachers.map(t => ({...t, fullName: `${t.fName} ${t.lName}`}));
    this.representativeList = teachersByRole.map(t => ({...t, fullName: `${t.fName} ${t.lName}`}));

    // Default the representativeName to currentTeacher if empty
    if (!this.formData.representativeName && this.currentTeacher) {
      this.formData.representativeName = `${this.currentTeacher.fName} ${this.currentTeacher.lName}`.trim();
      this.formData.representativeEmail = this.currentTeacher.email;
    }

    // Default school to current teacher's school if empty
    if (!this.formData.school && this.currentTeacher?.school) {
      const teacherSchool = this.currentTeacher.school.trim().toLowerCase();
      const match = this.schools.find(s => s.name.toLowerCase().startsWith(teacherSchool));
      if (match) {
        this.formData.school = match.name;
      }
    }
    this.filterTeacher();
  }

  filterTeacher() {
    const ghsName = this.formData.representativeName;
    const addName = '';

    this.teacherName = this.teacherFullList
      .filter(t => t.fullName !== ghsName && !addName.includes(t.fullName))
      .sort((a: any, b: any) => a.fName.localeCompare(b.fName));
  }

  onTeacherNameChanged() {
    const selectedTeacher = this.representativeList.find(
      t => t.fullName === this.formData.representativeName
    );

    if (selectedTeacher) {
      this.formData.representativeEmail = selectedTeacher.email;
    } else {
      this.formData.representativeEmail = '';
    }
  }

  extraRepresentatives: any[] = [];

  fillExtraRepresentatives() {
    const selectedEmails = [
      this.formData.representativeEmail,
      this.formData.rep2Email,
      this.formData.rep3Email
    ].filter(Boolean); // remove undefined/null

    this.extraRepresentatives = this.representativeList;
  }

  calculateParticipationHours() {
    const students = this.formData.numStudentsInProject || 0;
    const duration = this.formData.eventDuration || 0;
    this.formData.numParticipationHours = +(students * duration).toFixed(2);
  }

}
